import { useWhiteLabelContext } from "../../../WhiteLabelContext";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useSalesMarkup } from "../../../hooks/useSalesMarkup";
import { useTranslation } from "react-i18next";
import { Role } from "../../../core/models/entities/Role";
import {
  getProductPrice,
  ProductPrice,
} from "../../../core/models/entities/BaseLogo";

interface PriceTierNudgeProps {
  prices: ProductPrice[] | undefined;
  currentTotal: number;
  // When provided, the CTA renders as a clickable button that requests `needed`
  // additional pieces (the gap to the next tier). Without it, the nudge is
  // informational only.
  onAddClick?: (needed: number) => void;
}

export function PriceTierNudge({
  prices,
  currentTotal,
  onAddClick,
}: PriceTierNudgeProps) {
  const { t } = useTranslation();
  const { whiteLabel } = useWhiteLabelContext();
  const { role } = useProfileContext();
  const isCustomer = role === Role.EndUser;
  // Sales price markup (flag priceMarkupEnabled): the dealer's tier prices
  // are shown as sales prices (same factor on every tier, so the savings %
  // is unchanged). Inert while off.
  const markup = useSalesMarkup();

  if (!prices || prices.length <= 1) return null;

  const sortedPrices = [...prices].sort(
    (a, b) => a.minQuantity - b.minQuantity,
  );

  const currentTier = getProductPrice(prices, currentTotal);
  if (!currentTier) return null;

  const displayPrice = (tier: ProductPrice) =>
    markup.active
      ? markup.price(tier.adjustedPrice, tier.adjustedCustomerPrice)
      : isCustomer && tier.adjustedCustomerPrice != null
        ? tier.adjustedCustomerPrice
        : tier.adjustedPrice;

  const currentPrice = displayPrice(currentTier);
  const currency = currentTier.currency || "DKK";
  const nextTier = sortedPrices.find((p) => p.minQuantity > currentTotal);

  if (!nextTier) {
    return (
      <div className="mt-3 pt-2 border-t border-dashed border-gray-200">
        <div className="flex items-center justify-center gap-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded-md py-1.5">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <circle cx="5" cy="5" r="5" fill="currentColor" fillOpacity="0.15" />
            <path
              d="M3 5.2L4.5 6.5L7 3.5"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("compLogoDesign.bestPricePerPiece", {
            currency,
            price: currentPrice.toFixed(2),
          })}
        </div>
      </div>
    );
  }

  const nextPrice = displayPrice(nextTier);
  const needed = nextTier.minQuantity - currentTotal;
  const savingsPercent = Math.round(
    ((currentPrice - nextPrice) / currentPrice) * 100,
  );

  const prevMin = currentTier.minQuantity || 1;
  const range = nextTier.minQuantity - prevMin;
  const progress =
    range > 0 ? Math.min(((currentTotal - prevMin) / range) * 100, 100) : 0;

  const brandColor = whiteLabel.colorHex;

  const cta = (
    <div className="text-[11px] font-semibold" style={{ color: brandColor }}>
      {t("compLogoDesign.addMoreAndSave", {
        count: needed,
        percent: savingsPercent,
      })}
    </div>
  );

  const ctaWrapperStyle = {
    backgroundColor: `${brandColor}10`,
    borderLeft: `3px solid ${brandColor}`,
  };

  return (
    <div className="mt-3 pt-2 border-t border-dashed border-gray-200">
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-[10px] text-gray-400">
          {t("compLogoDesign.currentTierLine", {
            total: currentTotal,
            currency,
            price: currentPrice.toFixed(2),
          })}
        </span>
      </div>

      <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden mb-2">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${Math.max(progress, 6)}%`,
            backgroundColor: brandColor,
          }}
        />
      </div>

      {onAddClick ? (
        <button
          onClick={() => onAddClick(needed)}
          className="w-full text-left rounded-md px-2 py-1.5 transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
          style={ctaWrapperStyle}
        >
          {cta}
        </button>
      ) : (
        <div
          className="w-full text-left rounded-md px-2 py-1.5"
          style={ctaWrapperStyle}
        >
          {cta}
        </div>
      )}
    </div>
  );
}
